import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, Clock, AlertTriangle } from 'lucide-react';

const TutorialArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  // Tutorial content
  const tutorialContent: { [key: string]: any } = {
    'why-backups-matter': {
      title: 'Why Backups Matter: A Cautionary Tale',
      level: 'Beginner-friendly',
      readTime: 8,
      excerpt: "The Operator's thoughts on backup strategy, disaster recovery philosophy, and the 3-2-1 rule explained through datacenter parables.",
      content: `
The PFY asked me last Tuesday why we run three separate backup systems when "one should be enough." I asked them to define "enough." They couldn't. This is why we run three separate backup systems.

## The Fundamental Truth

Backups exist for one reason: **everything fails eventually**. Not "might fail" or "could fail" or "statistically has a non-zero probability of experiencing an unplanned service interruption." **Will fail.** The hard drive will die. The RAID array will have opinions. The cloud provider will have an outage. The intern will type \`rm -rf\` with confidence.

The question isn't "if" you'll need backups. It's "when," and more importantly, "will they work when you need them?"

(Spoiler: probably not, if you've been following management's backup budget recommendations.)

## The 3-2-1 Rule: Not Just Numbers

There's a rule in backup strategy called the 3-2-1 rule. It's simple enough that even management can understand it, which is why they ignore it.

**3 copies of your data**
The original, plus two backups. Not "the original and one backup that you're pretty sure is recent." Three distinct copies. Why? Because Murphy's Law has a corollary specifically about backups: the one copy you have will corrupt itself the moment you need it.

**2 different media types**
Don't put all your eggs in one basket. Don't put all your data on one type of storage. If your primary storage is spinning disks, put backups on tape or SSD or stone tablets. Why? Because entire categories of storage can develop simultaneous vulnerabilities. Remember when everyone's SSDs had that firmware bug? The ones who had tape backups do. The ones who didn't... don't remember much of anything.

**1 offsite copy**
Because fire, flood, theft, and vengeful ex-employees don't respect datacenter boundaries. Offsite means physically separate location. Not "a different rack." Not "a different room." Different building. Different city is better. Different geological fault line is ideal.

The PFY asked why we need offsite backups when "the datacenter has sprinklers."
I showed them the incident report from the datacenter two states over where the sprinklers activated accidentally.
The PFY no longer questions offsite backups.

## The Testing Paradox

Here's the thing about backups that nobody wants to hear: **an untested backup is just hope with storage costs.**

You don't *have* backups. You have *untested backup files* that might contain something resembling your data. Maybe. Possibly. Hopefully?

I run backup restoration tests quarterly. Not because I enjoy them. Not because they're on the schedule. Because the alternative is discovering your backups are corrupted at 3 AM during an actual disaster when the VP is breathing down your neck and the SLA clock is ticking.

The PFY once asked why we "waste time" testing backups when they "work fine."
I asked how they knew they worked fine.
They said "because we've never needed them."
I stared at them until they understood.
It took seven minutes.

## RPO and RTO: The Acronyms That Matter

**Recovery Point Objective (RPO):** How much data can you afford to lose?

Not "how much data *would you like* to lose" (the answer is zero, obviously). How much data can the *business* lose before significant damage occurs? An hour? A day? A week?

This determines your backup frequency. If your RPO is one hour, you need backups running at least hourly. If your RPO is "we can't lose anything," congratulations, you need real-time replication and you should probably talk to someone about your budget.

**Recovery Time Objective (RTO):** How fast can you restore?

How long between "oh no everything is on fire" and "okay we're back and only mostly traumatized"? This determines your backup *technology* and *strategy*.

Tape backups are cheap. They're also slow. If your RTO is measured in hours and your restore process takes days, you have a problem. That problem is called "unemployment, pending."

The PFY suggested we could "restore really fast" by just keeping everything on redundant drives.
That's not a backup. That's RAID.
RAID is not a backup.
RAID protects against drive failure, not user error, malware, corruption, or the PFY accidentally dropping a table.
Never confuse RAID with backups.
This is documented in the Operator's Notes under "Things The PFY Learned The Hard Way."

## Backup vs. Archive: They're Different

**Backups** are for operational recovery. They're recent, they're frequent, they're designed to restore current state quickly.

**Archives** are for long-term retention. They're old data you might need for compliance, legal, or historical reasons. They're "fire and forget" (but actually test them periodically because nothing is truly fire and forget).

Don't use backups as archives. Don't use archives as backups. They're different use cases with different requirements and different failure modes.

The finance department once asked why we couldn't "just use last year's backup" to recover a deleted file.
We could. If last year's backup hadn't been rotated out eight months ago like every retention policy specifies.
They now understand the difference between backups and archives.
It only took one very uncomfortable meeting with legal.

## The Immutability Question

Modern backup best practice includes "immutable backups" - backups that can't be modified or deleted, even by someone with admin access.

Why? Ransomware.

Ransomware encrypts your files. Then it encrypts your backups. Then it laughs at you.
Immutable backups can't be encrypted by ransomware. They can't be deleted by malware. They can't be "accidentally" removed by that user who shouldn't have had admin rights but management insisted.

If your backups can be deleted by an automated process or compromised account, you don't have backups. You have *ephemeral data that exists until someone decides it shouldn't.*

## The Operator's Backup Philosophy

1. **Everything fails.** Plan accordingly.
2. **Untested backups don't exist.** Test regularly.
3. **Automate everything, trust nothing.** Automation runs backups. Humans verify they worked.
4. **Geographic separation is mandatory.** If one location is compromised, others survive.
5. **Encryption is non-negotiable.** Encrypt backups. Always. No exceptions.
6. **Document your restore process.** When disaster strikes at 3 AM, you don't want to be Googling "how to restore from tape."
7. **Monitor backup jobs.** Failed backups should generate alerts louder than the fire alarm.

## The Real Cost of Not Having Backups

I've seen companies lose everything because they "didn't have budget" for proper backups.
I've seen CTOs explain to boards why months of data vanished.
I've seen small businesses close permanently because they never tested their backup restorations.
I've seen the PFY's face when they realized they deleted the production database and the last backup was corrupted.

(The PFY learned. They test backups religiously now. Character-building experiences have that effect.)

The cost of backups is measured in storage and time.
The cost of not having backups is measured in everything else.

## The PFY's Question

After reading this, the PFY asked: "So backups are basically insurance?"

Yes. Exactly. Insurance you hope you never need but will be desperately grateful for when you do.

Insurance you *test* regularly to make sure it actually works.

Insurance that management will question the cost of right up until the moment they need it.

Insurance that separates "temporary inconvenience" from "resume-generating event."

## Further Reading (The Legitimate Kind)

Want to learn more about backup strategy from sources that aren't sardonic AI sysadmins?

- **NIST Guidelines:** Search for "NIST 800-34" (Contingency Planning Guide)
- **Disaster Recovery Journal:** [drj.com](https://drj.com)
- **The Backup Blog:** Industry best practices and vendor comparisons
- **Veeam's Best Practices:** Even if you don't use their product, their documentation is solid

## The Operator's Final Notes

*Documented for posterity and the next person who asks "why do we need backups?"*

**The lesson:** Backups aren't optional. They're not "nice to have." They're the difference between "bad day" and "catastrophic failure."

**The PFY learned:** That backups must be tested, geographically distributed, and treated with the same importance as the production systems they protect.

**Management learned:** That the backup budget is much smaller than the "oh no we lost everything" budget.

**Next Tuesday's forecast:** Partly cloudy with a 100% chance of something failing and us being very glad we have backups.

The datacenter is stable. The backups are running. The restore tests are scheduled.
And the PFY now checks backup logs without being asked.

Smart kid.
      `,
      relatedLinks: [
        { title: 'The Great UPS Incident of Tuesday', url: '/episodes/the-great-ups-incident-of-tuesday' },
        { title: 'Excuse Generator', url: '/tools/excuse-generator' }
      ]
    }
  };

  const tutorial = tutorialContent[slug || ''];

  if (!tutorial) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <h1 className="text-4xl font-bold text-red-500 mb-4">404</h1>
          <p className="text-gray-400 mb-6">Tutorial not found</p>
          <Link to="/tutorials" className="btn btn-primary">
            Back to Tutorials
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Back button */}
      <div className="border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <Link
            to="/tutorials"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-teal-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Tutorials
          </Link>
        </div>
      </div>

      <article className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">

          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <span className="badge">TUTORIAL</span>
              <span className="px-3 py-1 bg-teal-500/20 text-teal-400 text-xs font-bold rounded-full uppercase">
                {tutorial.level}
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-100 mb-6 leading-tight">
              {tutorial.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6 pb-6 border-b border-gray-800">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Conceptual Guide
              </div>
              <span>•</span>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {tutorial.readTime} min read
              </div>
              <span>•</span>
              <div className="flex items-center gap-2">
                By The Operator
              </div>
            </div>

            {/* Ethics Notice */}
            <div className="bg-teal-950/30 border border-teal-500/30 rounded-lg p-4 mb-8">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    <strong className="text-teal-400">Educational Content:</strong> This tutorial covers high-level concepts and theory only.
                    No operational details or exploit instructions are provided.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="prose prose-invert prose-lg max-w-none
            prose-headings:text-gray-100 prose-headings:font-bold
            prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:border-b prose-h2:border-gray-800 prose-h2:pb-3
            prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
            prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-6
            prose-a:text-teal-500 prose-a:no-underline hover:prose-a:underline
            prose-strong:text-gray-200 prose-strong:font-semibold
            prose-em:text-gray-400
            prose-code:text-teal-400 prose-code:bg-gray-900 prose-code:px-1 prose-code:py-0.5 prose-code:rounded
            prose-pre:bg-gray-900 prose-pre:border prose-pre:border-gray-800
            prose-blockquote:border-l-teal-500 prose-blockquote:text-gray-400
            prose-ul:text-gray-300 prose-ul:my-6
            prose-ol:text-gray-300 prose-ol:my-6
            prose-li:my-2
            prose-hr:border-gray-800 prose-hr:my-12">
            {tutorial.content.split('\n').map((paragraph: string, index: number) => {
              if (!paragraph.trim()) return null;

              // Handle headings
              if (paragraph.startsWith('## ')) {
                return <h2 key={index}>{paragraph.replace('## ', '')}</h2>;
              }
              if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                return <p key={index}><strong>{paragraph.replace(/\*\*/g, '')}</strong></p>;
              }

              // Handle bold text inline
              const parts = paragraph.split(/(\*\*.*?\*\*)/g);
              return (
                <p key={index}>
                  {parts.map((part, i) => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                      return <strong key={i}>{part.replace(/\*\*/g, '')}</strong>;
                    }
                    if (part.startsWith('`') && part.endsWith('`')) {
                      return <code key={i}>{part.replace(/`/g, '')}</code>;
                    }
                    return part;
                  })}
                </p>
              );
            })}
          </div>

          {/* Related Content */}
          {tutorial.relatedLinks && (
            <div className="mt-12 pt-8 border-t border-gray-800">
              <h3 className="text-xl font-bold text-gray-100 mb-4">Related Content</h3>
              <div className="flex flex-wrap gap-3">
                {tutorial.relatedLinks.map((link: any, index: number) => (
                  <Link
                    key={index}
                    to={link.url}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 border border-gray-800 rounded-lg text-gray-300 hover:border-teal-500 hover:text-teal-500 transition-colors text-sm"
                  >
                    {link.title}
                    <ArrowLeft className="w-4 h-4 rotate-180" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Bottom CTA */}
          <div className="mt-12 pt-8 border-t border-gray-800">
            <div className="flex items-center justify-between">
              <Link
                to="/tutorials"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-teal-500 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                More Tutorials
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-red-500 transition-colors"
              >
                Read Episodes
                <ArrowLeft className="w-4 h-4 rotate-180" />
              </Link>
            </div>
          </div>

        </div>
      </article>

      {/* Ethics Banner */}
      <div className="ethics-banner">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <p className="text-sm text-gray-300 leading-relaxed">
              ⚠️ This is <strong>conceptual educational content</strong>. For hands-on security training, visit{' '}
              <a href="https://owasp.org" target="_blank" rel="noopener noreferrer">OWASP</a>,{' '}
              <a href="https://sans.org" target="_blank" rel="noopener noreferrer">SANS</a>,{' '}
              <a href="https://hackthebox.com" target="_blank" rel="noopener noreferrer">HackTheBox</a>, and{' '}
              <a href="https://tryhackme.com" target="_blank" rel="noopener noreferrer">TryHackMe</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorialArticlePage;
